import toastr from 'react-hot-toast';
import { useMutation } from 'react-query';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useBatchUpdateOperations } from 'hooks/batchOperations';
import { useSparkStepOrderHelper } from 'stores/sparkBuilderStore';
import type { SparkStep } from 'types/SparkStep';
import { chunkArray } from 'helpers/sparkStep';

type mutationVariables = { sparkStepID: string; stepOrder: number };

type Response = { 'atomic:results': { data: SparkStep }[] };

export function useBatchSparkStepOrder() {
  const { mutateAsync: updateBatchSparkStepOrder } = useBatchUpdateOperations();
  const sparkStepOrderHelper = useSparkStepOrderHelper();

  const updateSparkStepOrder = async (stepOrder: mutationVariables[]) => {
    const sparkOrderArray = stepOrder.map((step) => ({
      type: SPARK_RESOURCE_TYPES.SPARK_STEPS,
      attributes: { stepOrder: step.stepOrder, id: step.sparkStepID },
    }));

    const chunkedArray = chunkArray(sparkOrderArray, 20);
    const lastOrder = sparkOrderArray.at(-1);
    for (const chunk of chunkedArray) {
      try {
        const updatedResponse = (await updateBatchSparkStepOrder(
          chunk
        )) as Response;

        const updatedSparkSteps: SparkStep[] = updatedResponse[
          'atomic:results'
        ].map((data) => ({
          ...data.data,
        }));
        const isQueryFinished =
          chunk.at(-1)?.attributes.id === lastOrder?.attributes.id;
        sparkStepOrderHelper.onSuccess(updatedSparkSteps, isQueryFinished);
      } catch (error) {
        sparkStepOrderHelper.onError();
        if (error instanceof Error) {
          toastr.error(`Failed to update Spark Steps: ${error.message}`);
        }
        break;
      }
    }
  };

  return useMutation('updateSparkStepOrder', updateSparkStepOrder, {
    onMutate: (stepOrderData) => {
      sparkStepOrderHelper.onMutate(stepOrderData);
    },
  });
}
